import { LocalStorageKeys } from '../types/LocalStorage'
import { SessionStorageKeys } from '../types/SessionStorage'
import { HttpStatusCode } from './types'
import { reportError } from '../utils/reportError'
import { getRedirectUrlSearch } from '../utils/url'

export abstract class ApiService {
    protected baseUrl: string

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl
    }

    protected authHeader() {
        const auth = JSON.parse(localStorage.getItem(LocalStorageKeys.AuthenticationCode))

        if (auth && auth.token) {
            return { Authorization: 'Bearer ' + auth.token }
        }
        throw new Error('unauthorized')
    }

    //we extract the body in that way because the BE returns not valid JSON for the DELETE requests,
    //TODO: BE invalid json response fix
    protected async getBody(response: Response) {
        const contentType = response.headers.get('content-type')

        if (contentType && contentType.includes('application/json')) {
            return await response.json()
        }

        return { message: await response.text() }
    }

    protected async handleResponse(response: Response, method: 'get' | 'post' | 'put' | 'delete' | 'patch' = 'get') {
        if (response.status >= 500) {
            reportError(`[API "${method}" call, URL: ${response.url}]: STATUS ${response.status}`)
        }

        const data = await this.getBody(response)

        if (!response.ok) {
            if (response.status === HttpStatusCode.Unauthorized && window.location.pathname !== '/login') {
                window.location.href = '/login' + getRedirectUrlSearch()

                localStorage.removeItem(LocalStorageKeys.AuthenticationCode)
                localStorage.setItem(LocalStorageKeys.LogoutEvent, 'logout-event' + Math.random())
                sessionStorage.setItem(SessionStorageKeys.Message, 'Unauthorized access. Please login again.')
                return Promise.reject({ message: 'Unauthorized access. Please login again.' })
            }
            const error =
                (data && {
                    status: response.status,
                    message: data.message,
                    attemptsLeft: data.attemptsLeft,
                    cooldown: data.cooldown,
                    details: data.details
                }) ||
                response.statusText
            return Promise.reject(error)
        }
        sessionStorage.removeItem(SessionStorageKeys.Message)

        return data
    }

    protected async get(url: string, headers?: any, differentBaseUrl?: boolean, controller?: AbortController) {
        const header = this.authHeader()

        const requestOptions = {
            method: 'GET',
            headers: { ...header, ...headers },
            signal: controller?.signal
        }
        const response = differentBaseUrl ? await fetch(`${url}`, requestOptions) : await fetch(`${this.baseUrl}${url}`, requestOptions)

        return this.handleResponse(response, 'get')
    }

    protected async post(url: string, dto?: any, differentBaseUrl?: boolean, isIncognito = false) {
        const header = isIncognito ? {} : this.authHeader()

        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...header },
            body: JSON.stringify(dto)
        }

        const response = await fetch(differentBaseUrl ? `${url}` : `${this.baseUrl}${url}`, requestOptions)

        return this.handleResponse(response, 'post')
    }

    protected async put(url: string, dto?: any) {
        const header = this.authHeader()
        const requestOptions = {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...header },
            body: JSON.stringify(dto)
        }
        const response = await fetch(`${this.baseUrl}${url}`, requestOptions)

        return this.handleResponse(response, 'put')
    }

    protected async patch(url: string, dto?: any) {
        const header = this.authHeader()

        const requestOptions = {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...header },
            body: JSON.stringify(dto)
        }
        const response = await fetch(`${this.baseUrl}${url}`, requestOptions)

        return this.handleResponse(response, 'patch')
    }

    protected async del<T>(url: string): Promise<T> {
        const header = this.authHeader()

        const requestOptions = {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json', ...header }
        }
        const response = await fetch(`${this.baseUrl}${url}`, requestOptions)

        return this.handleResponse(response, 'delete')
    }
}
